import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Box,
  Stack,
  Button,
  Chip,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Rating,
  Skeleton,
  Pagination,
  Dialog,
  DialogContent,
  IconButton,
  Autocomplete
} from '@mui/material';
import {
  Search,
  Star,
  Diamond,
  WhatsApp,
  Phone,
  Close,
  ChevronLeft,
  ChevronRight
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { productService } from '../services/productService';
import categorieService from '../services/categorieService';
import ImageCard from '../components/ImageCard';

const VIP = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(21);
  
  // États pour la modal d'images
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [validImages, setValidImages] = useState([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  
  // État pour suivre l'index de l'image actuelle pour chaque produit dans les cartes
  const [productImageIndices, setProductImageIndices] = useState({});

  // Charger les catégories au montage
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setCategoriesLoading(true);
        const data = await categorieService.getCategories();
        const categoriesList = Array.isArray(data) ? data : (data.categories || []);
        setCategories(categoriesList);
      } catch (error) {
        console.error('Error fetching categories:', error);
        setCategories([]);
      } finally {
        setCategoriesLoading(false);
      }
    };
    fetchCategories();
  }, []);

  useEffect(() => {
    fetchProducts();
  }, [search, category, sortBy]);

  // Calcul de la pagination
  const totalPages = Math.ceil(products.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentProducts = products.slice(startIndex, endIndex);

  // Fonction pour changer de page
  const handlePageChange = (event, page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Reset de la page quand on change de recherche, catégorie ou tri
  useEffect(() => {
    setCurrentPage(1);
  }, [search, category, sortBy]);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const response = await productService.getProducts({ search, category, sortBy });
      // Filtrer uniquement les produits VIP
      const vipProducts = (response.products || []).filter(product => product.isVip === true);
      setProducts(vipProducts);
    } catch (error) {
      console.error('Error fetching VIP products:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fonction helper pour filtrer les images valides
  const filterValidImages = useCallback((images) => {
    return images.filter(img => {
      return img && typeof img === 'string' && img.trim() !== '';
    });
  }, []);

  // Fonctions pour naviguer entre les images dans les cartes
  const handleProductImageNext = (e, productId, totalImages) => {
    e.stopPropagation();
    setProductImageIndices(prev => ({
      ...prev,
      [productId]: ((prev[productId] || 0) + 1) % totalImages
    }));
  };

  const handleProductImagePrev = (e, productId, totalImages) => {
    e.stopPropagation();
    setProductImageIndices(prev => ({
      ...prev,
      [productId]: ((prev[productId] || 0) - 1 + totalImages) % totalImages
    }));
  };

  const handleImageClick = (product) => {
    const images = product?.images || [];
    const validImagesArray = filterValidImages(images);

    if (validImagesArray.length === 0) {
      return;
    }

    // Précharger toutes les images
    validImagesArray.forEach(imgSrc => {
      if (imgSrc) {
        const img = new Image();
        img.src = imgSrc;
      }
    });

    setSelectedProduct(product);
    setValidImages(validImagesArray);
    setCurrentImageIndex(0);
    setImageModalOpen(true);
  };

  const handleCloseModal = useCallback(() => {
    setImageModalOpen(false);
    setSelectedProduct(null);
    setValidImages([]);
    setCurrentImageIndex(0);
  }, []);

  const handleNextImage = useCallback(() => {
    if (validImages.length === 0) return;
    setCurrentImageIndex((prevIndex) => (prevIndex + 1) % validImages.length);
  }, [validImages.length]);

  const handlePreviousImage = useCallback(() => {
    if (validImages.length === 0) return;
    setCurrentImageIndex((prevIndex) => (prevIndex - 1 + validImages.length) % validImages.length);
  }, [validImages.length]);

  const handleThumbnailClick = useCallback((index) => {
    if (index >= 0 && index < validImages.length && index !== currentImageIndex) {
      setCurrentImageIndex(index);
    }
  }, [validImages.length, currentImageIndex]);

  const ProductSkeleton = () => (
    <Card>
      <Skeleton variant="rectangular" height={200} />
      <CardContent>
        <Skeleton variant="text" height={32} />
        <Skeleton variant="text" height={20} />
        <Skeleton variant="text" height={20} />
        <Skeleton variant="rectangular" height={36} sx={{ mt: 2 }} />
      </CardContent>
    </Card>
  );

  // Corriger l'index si invalide
  useEffect(() => {
    if (validImages.length > 0 && currentImageIndex >= validImages.length) {
      setCurrentImageIndex(0);
    }
  }, [validImages.length, currentImageIndex]);

  const modalImages = useMemo(() => {
    return validImages.length > 0 ? validImages : (selectedProduct?.images || []);
  }, [validImages, selectedProduct]);

  const safeIndex = useMemo(() => {
    if (modalImages.length === 0) return 0;
    return currentImageIndex >= modalImages.length ? 0 : currentImageIndex;
  }, [modalImages.length, currentImageIndex]);

  const ImageModal = () => {
    const currentImage = modalImages[safeIndex];

    return (
      <Dialog
        open={imageModalOpen}
        onClose={handleCloseModal}
        maxWidth={false}
        keepMounted={false}
        disableEscapeKeyDown={false}
        PaperProps={{
          sx: {
            width: '85vw',
            maxWidth: '900px',
            maxHeight: '85vh',
            bgcolor: 'rgba(0, 0, 0, 0.95)',
            position: 'relative',
            margin: 0,
            transition: 'none !important'
          }
        }}
        BackdropProps={{
          sx: {
            transition: 'none !important',
            animation: 'none !important',
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            opacity: '1 !important'
          }
        }}
        sx={{
          '& .MuiDialog-container': {
            transition: 'none !important',
            animation: 'none !important'
          },
          '& .MuiDialog-root': {
            transition: 'none !important',
            animation: 'none !important'
          },
          '& .MuiBackdrop-root': {
            transition: 'none !important',
            animation: 'none !important',
            opacity: '1 !important'
          },
          '& .MuiDialog-paper': {
            transition: 'none !important',
            animation: 'none !important'
          },
          '& .MuiDialog-paperScrollBody': {
            transition: 'none !important',
            animation: 'none !important'
          }
        }}
      >
        <DialogContent
          sx={{
            p: 0,
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '70vh',
            maxHeight: '75vh',
            overflow: 'hidden',
            transition: 'none !important',
            '&.MuiDialogContent-root': {
              transition: 'none !important'
            }
          }}
        >
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              handleCloseModal();
            }}
            sx={{
              position: 'absolute',
              top: 16,
              right: 16,
              zIndex: 10,
              bgcolor: '#f97316',
              color: 'white',
              '&:hover': {
                bgcolor: 'rgba(171, 56, 56, 0.3)'
              }
            }}
          >
            <Close />
          </IconButton>

          {modalImages.length > 1 && (
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                handlePreviousImage();
              }}
              disabled={modalImages.length <= 1}
              sx={{
                position: 'absolute',
                left: 8,
                top: '50%',
                transform: 'translateY(-50%)',
                zIndex: 10,
                bgcolor: '#f97316',
                color: 'white',
                '&:hover': {
                  bgcolor: 'white'
                },
                '&:active': {
                  transform: 'translateY(-50%) scale(0.95)'
                },
                '&.Mui-disabled': {
                  bgcolor: 'rgba(255, 255, 255, 0.5)',
                  color: 'rgba(0, 0, 0, 0.3)'
                },
                transition: 'all 0.15s ease'
              }}
            >
              <ChevronLeft />
            </IconButton>
          )}

          {modalImages.length > 1 && (
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                handleNextImage();
              }}
              disabled={modalImages.length <= 1}
              sx={{
                position: 'absolute',
                right: 8,
                top: '50%',
                transform: 'translateY(-50%)',
                zIndex: 10,
                bgcolor: '#f97316',
                color: 'white',
                '&:hover': {
                  bgcolor: '#f97316'
                },
                '&:active': {
                  transform: 'translateY(-50%) scale(0.95)'
                },
                '&.Mui-disabled': {
                  bgcolor: 'rgba(255, 255, 255, 0.5)',
                  color: 'rgba(0, 0, 0, 0.3)'
                },
                transition: 'all 0.15s ease'
              }}
            >
              <ChevronRight />
            </IconButton>
          )}

          <Box
            sx={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden',
              position: 'relative',
              userSelect: 'none'
            }}
          >
            {currentImage ? (
              <Box
                component="img"
                key={`img-${safeIndex}`}
                src={currentImage}
                alt={selectedProduct?.name || 'Product image'}
                loading="eager"
                onError={(e) => {
                  if (!e.target.src.includes('no_media')) {
                    e.target.src = '/img/no_media.jpg';
                  }
                }}
                onLoad={(e) => {
                  if (modalImages.length > 1) {
                    const nextIndex = (safeIndex + 1) % modalImages.length;
                    const prevIndex = (safeIndex - 1 + modalImages.length) % modalImages.length;

                    [nextIndex, prevIndex].forEach(idx => {
                      if (modalImages[idx] && modalImages[idx] !== currentImage) {
                        const preloadImg = new Image();
                        preloadImg.src = modalImages[idx];
                      }
                    });
                  }
                }}
                draggable={false}
                sx={{
                  maxWidth: '100%',
                  maxHeight: '70vh',
                  objectFit: 'contain',
                  transition: 'opacity 0.15s ease-in-out',
                  userSelect: 'none',
                  pointerEvents: 'auto',
                  imageRendering: 'auto',
                  display: 'block',
                  visibility: 'visible'
                }}
              />
            ) : (
              <Box
                component="img"
                src="/img/no_media.jpg"
                alt="No image"
                draggable={false}
                sx={{
                  maxWidth: '100%',
                  maxHeight: '70vh',
                  objectFit: 'contain'
                }}
              />
            )}
          </Box>

          {modalImages.length > 1 && (
            <Box
              sx={{
                position: 'absolute',
                bottom: 8,
                right: 8,
                bgcolor: 'rgba(0,0,0,0.7)',
                color: 'white',
                px: 1.5,
                py: 0.5,
                borderRadius: 1,
                fontSize: '0.75rem',
                fontWeight: 500,
                zIndex: 10
              }}
            >
              {safeIndex + 1} / {modalImages.length}
            </Box>
          )}
        </DialogContent>

        {modalImages.length > 1 && (
          <Box
            sx={{
              p: 1.5,
              display: 'flex',
              gap: 0.75,
              overflowX: 'auto',
              borderTop: '1px solid',
              borderColor: 'rgba(255, 255, 255, 0.2)',
              bgcolor: 'rgba(0, 0, 0, 0.5)',
              width: '100%',
              '&::-webkit-scrollbar': {
                height: '6px'
              },
              '&::-webkit-scrollbar-track': {
                background: 'rgba(255, 255, 255, 0.1)',
                borderRadius: '3px'
              },
              '&::-webkit-scrollbar-thumb': {
                background: 'rgba(255, 255, 255, 0.3)',
                borderRadius: '3px',
                '&:hover': {
                  background: 'rgba(255, 255, 255, 0.5)'
                }
              }
            }}
          >
            {modalImages.map((image, index) => {
              const isActive = safeIndex === index;
              return (
                <Box
                  key={`thumb-${index}-${image?.substring(0, 30) || index}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleThumbnailClick(index);
                  }}
                  sx={{
                    minWidth: 70,
                    width: 70,
                    height: 50,
                    borderRadius: 1,
                    overflow: 'hidden',
                    cursor: isActive ? 'default' : 'pointer',
                    border: isActive
                      ? '2px solid #1976d2'
                      : '2px solid transparent',
                    opacity: isActive ? 1 : 0.7,
                    flexShrink: 0,
                    '&:hover': {
                      opacity: isActive ? 1 : 1,
                      borderColor: isActive ? '#1976d2' : 'rgba(255, 255, 255, 0.5)'
                    },
                    transition: 'all 0.2s ease-in-out'
                  }}
                >
                  <Box
                    component="img"
                    src={image}
                    alt={`${selectedProduct?.name || 'Image'} ${index + 1}`}
                    onError={(e) => {
                      if (!e.target.src.includes('no_media')) {
                        e.target.src = '/img/no_media.jpg';
                      }
                    }}
                    loading="lazy"
                    sx={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      display: 'block',
                      pointerEvents: 'none'
                    }}
                  />
                </Box>
              );
            })}
          </Box>
        )}
      </Dialog>
    );
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header with Search and Filters */}
      <Box sx={{ mb: 6 }}>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { xs: 'flex-start', md: 'center' },
          justifyContent: 'space-between',
          gap: 3,
          mb: 2
        }}>
          <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', gap: 2 }}>
            <Diamond sx={{ fontSize: 40, color: 'warning.main' }} />
            <Typography variant="h2" gutterBottom sx={{ fontWeight: 700, mb: 0 }}>
              Produits VIP
            </Typography>
          </Box>
          
          {/* Search and Filters */}
          <Stack 
            direction={{ xs: 'column', sm: 'row' }} 
            spacing={2} 
            alignItems="center"
            sx={{ 
              width: { xs: '100%', md: 'auto' },
              minWidth: { md: 400 }
            }}
          >
            <TextField
              placeholder="Rechercher des produits VIP..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                )
              }}
              sx={{ 
                flexGrow: 1, 
                maxWidth: { xs: '100%', sm: 400 },
                width: { xs: '100%', sm: 'auto' }
              }}
            />
            <Autocomplete
              options={[{ id: '', code: '', libelle: 'Toutes' }, ...categories]}
              getOptionLabel={(option) => option.libelle || option.name || ''}
              value={
                category 
                  ? (categories.find(cat => (cat.code || cat.id) === category) || null)
                  : { id: '', code: '', libelle: 'Toutes' }
              }
              onChange={(event, newValue) => {
                if (!newValue || newValue.id === '') {
                  setCategory('');
                } else {
                  setCategory(newValue.code || newValue.id || '');
                }
              }}
              loading={categoriesLoading}
              disabled={categoriesLoading}
              isOptionEqualToValue={(option, value) => {
                if (option.id === '' && value.id === '') return true;
                return (option.code || option.id) === (value.code || value.id);
              }}
              sx={{ 
                minWidth: { xs: '100%', sm: 200, md: 250 },
                width: { xs: '100%', sm: 'auto' },
                '& .MuiAutocomplete-inputRoot': {
                  width: '100% !important',
                  minWidth: '100% !important'
                },
                '& .MuiAutocomplete-input': {
                  width: '100% !important'
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Catégorie"
                  placeholder={categoriesLoading ? "Chargement des catégories..." : "Sélectionnez une catégorie"}
                  sx={{ 
                    width: '100%',
                    minWidth: '100%',
                    maxWidth: '100%',
                    '& .MuiInputBase-root': {
                      width: '100% !important',
                      minWidth: '100% !important'
                    },
                    '& .MuiInputBase-input': {
                      width: '100% !important',
                      minWidth: '100% !important'
                    }
                  }}
                />
              )}
              renderOption={(props, option) => (
                <Box component="li" {...props}>
                  <Typography variant="body1">
                    {option.libelle || option.name}
                  </Typography>
                  {/* {option.code && option.id !== '' && (
                    <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                      {option.code}
                    </Typography>
                  )} */}
                </Box>
              )}
              noOptionsText={
                categoriesLoading
                  ? "Chargement des catégories..."
                  : "Aucune catégorie trouvée"
              }
            />
            <FormControl 
              sx={{ 
                minWidth: { xs: '100%', sm: 200, md: 250 },
                width: { xs: '100%', sm: 'auto' }
              }}
              fullWidth={false}
            >
              <InputLabel>Trier par</InputLabel>
              <Select
                value={sortBy}
                label="Trier par"
                onChange={(e) => setSortBy(e.target.value)}
              >
                <MenuItem value="newest">Plus récents</MenuItem>
                <MenuItem value="price-low">Prix croissant</MenuItem>
                <MenuItem value="price-high">Prix décroissant</MenuItem>
                <MenuItem value="rating">Mieux notés</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </Box>
      </Box>

      {/* Results */}
      {loading ? (
        <Grid container spacing={3} sx={{ display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
  gridAutoRows: '1fr' }}>
          {[...Array(9)].map((_, index) => (
            <Grid item xs={12} sm={6} lg={3} key={index}>
              <ProductSkeleton />
            </Grid>
          ))}
        </Grid>
      ) : products.length === 0 ? (
        <Box textAlign="center" sx={{ py: 8 }}>
          <Typography variant="h5" color="text.secondary" gutterBottom>
            Aucun produit VIP trouvé
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Essayez de modifier vos critères de recherche
          </Typography>
        </Box>
      ) : (
        <>
          <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" color="text.secondary">
              {products.length} produit{products.length > 1 ? 's' : ''} VIP trouvé{products.length > 1 ? 's' : ''}
              {totalPages > 1 && (
                <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                  (Page {currentPage} sur {totalPages})
                </Typography>
              )}
            </Typography>
          </Box>

          <Grid container spacing={3} sx={{ display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
  gridAutoRows: '1fr' }}>
            {currentProducts.map((product) => (
              <Grid item xs={12} sm={6} lg={3} key={product.id}>
                <Card 
                  sx={{ 
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-1px)',
                      boxShadow: 2
                    }
                  }}
                >
                  <Box 
                    sx={{ 
                      position: 'relative',
                      cursor: 'pointer'
                    }}
                    onClick={() => handleImageClick(product)}
                  >
                    {(() => {
                      // S'assurer que product.images est un tableau et filtrer les valeurs vides
                      const productImages = Array.isArray(product.images) 
                        ? product.images.filter(img => img && typeof img === 'string' && img.trim() !== '')
                        : [];
                      const currentIndex = productImageIndices[product.id] || 0;
                      const hasMultipleImages = productImages.length > 1;
                      // Utiliser l'image à l'index courant, ou la première, ou une image par défaut
                      const currentImage = productImages.length > 0 
                        ? (productImages[currentIndex] || productImages[0])
                        : '/img/no_media.jpg';
                      
                      return (
                        <>
                          <ImageCard
                            src={currentImage}
                            alt={product.name}
                            height={200}
                          />
                          
                          {/* Navigation entre images */}
                          {hasMultipleImages && (
                            <>
                              <IconButton
                                size="small"
                                onClick={(e) => handleProductImagePrev(e, product.id, productImages.length)}
                                sx={{
                                  position: 'absolute',
                                  left: 8,
                                  top: '50%',
                                  transform: 'translateY(-50%)',
                                  bgcolor: 'rgba(255,255,255,0.9)',
                                  '&:hover': { bgcolor: 'white' },
                                  zIndex: 2
                                }}
                              >
                                <ChevronLeft fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={(e) => handleProductImageNext(e, product.id, productImages.length)}
                                sx={{
                                  position: 'absolute',
                                  right: 8,
                                  top: '50%',
                                  transform: 'translateY(-50%)',
                                  bgcolor: 'rgba(255,255,255,0.9)',
                                  '&:hover': { bgcolor: 'white' },
                                  zIndex: 2
                                }}
                              >
                                <ChevronRight fontSize="small" />
                              </IconButton>
                              {/* Indicateur du nombre d'images */}
                              <Box
                                sx={{
                                  position: 'absolute',
                                  bottom: 8,
                                  right: 8,
                                  bgcolor: 'rgba(0,0,0,0.7)',
                                  color: 'white',
                                  px: 1,
                                  py: 0.5,
                                  borderRadius: 1,
                                  fontSize: '0.7rem',
                                  fontWeight: 500,
                                  zIndex: 2
                                }}
                              >
                                {currentIndex + 1} / {productImages.length}
                              </Box>
                            </>
                          )}
                        </>
                      );
                    })()}
                    
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        display: 'flex',
                        gap: 1,
                        zIndex: 2
                      }}
                    >
                      <Chip
                        label="VIP"
                        color="warning"
                        size="small"
                        sx={{ fontWeight: 600 }}
                      />
                    </Box>
                    {(!product.isAvailable || product.stockQuantity === 0) && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 4,
                          left: 4,
                          bgcolor: 'error.main',
                          color: 'white',
                          px: 1,
                          py: 0.5,
                          borderRadius: 1,
                          fontSize: '0.7rem',
                          fontWeight: 600,
                          zIndex: 3
                        }}
                      >
                        En rupture
                      </Box>
                    )}
                  </Box>
                  
                  <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                      {product.name}
                    </Typography>
                    <Typography 
                      variant="body2" 
                      color="text.secondary" 
                      paragraph
                      sx={{ 
                        flexGrow: 1,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden'
                      }}
                    >
                      {product.description}
                    </Typography>
                    
                    <Box sx={{ mb: 2 }}>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Rating 
                          value={product.averageRating || 0} 
                          precision={0.1} 
                          size="small" 
                          readOnly 
                        />
                        <Typography variant="body2" color="text.secondary">
                          ({product.totalReviews || 0})
                        </Typography>
                      </Stack>
                    </Box>

                    <Box sx={{ mt: 'auto' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h5" color="primary.main" sx={{ fontWeight: 700 }}>
                          {parseFloat(product.price || 0).toLocaleString('fr-FR', { useGrouping: true, minimumFractionDigits: 0, maximumFractionDigits: 0 })} FCFA
                        </Typography>
                        {/* <Typography variant="body2" color="text.secondary">
                          Stock: {product.stockQuantity || 0}
                        </Typography> */}
                                                <Typography
                          style={{
                            // textDecoration: product.isAvailable ? "none" : "line-through",
                            textDecoration: product.isAvailable ? "none" : "none",
                            color: product.isAvailable ? "green" : "red",
                          }}
                        >
                          {product.isAvailable ? (
                            <>
                              Disponible — Stock : {product.stockQuantity || 0}
                            </>
                          ) : (
                            <>
                              En rupture
                            </>
                          )}
                        </Typography>
                      </Box>
                      
                      <Stack direction="row" spacing={0.5}>
                        <Button
                          variant="outlined"
                          fullWidth
                          sx={{ background: 'linear-gradient(90deg, #9b5de5 0%, #f15bb5 100%)', color: 'white' }}
                          onClick={() => navigate(`/products/${product.id}`)}
                          disabled={product.stockQuantity === 0}
                        >
                          Détails
                        </Button>
                        {(() => {
                          // Si l'utilisateur a une boutique, utiliser le numéro de la boutique, sinon utiliser le numéro de l'utilisateur
                          const contactPhone = product.boutique 
                            ? (product.boutique.whatsapp || product.boutique.phone)
                            : product.user?.phone;
                          return contactPhone && (
                            <>
                              <Button
                                variant="contained"
                                fullWidth
                                startIcon={<WhatsApp />}
                                onClick={() => {
                                  const phoneNumber = contactPhone;
                                  const message = `Bonjour, je suis intéressé par le produit VIP "${product.name}"${product.boutique ? ` de ${product.boutique.name}` : ''}`;
                                  window.open(`https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`, '_blank');
                                }}
                                sx={{ 
                                  bgcolor: '#25D366',
                                  '&:hover': { bgcolor: '#128C7E' },
                                  '& .MuiButton-startIcon': {
                                    marginRight: '1px'
                                  }
                                }}
                              >
                                WhatsApp
                              </Button>
                              <Button
                                variant="outlined"
                                fullWidth
                                startIcon={<Phone />}
                                onClick={() => {
                                  const phoneNumber = contactPhone;
                                  window.open(`tel:${phoneNumber}`, '_self');
                                }}
                                sx={{ 
                                  bgcolor: 'blue.500',
                                  '&:hover': { borderColor: 'blue.500', bgcolor: 'white', color: 'blue.500' },
                                  '& .MuiButton-startIcon': {
                                    marginRight: '1px'
                                  }
                                }}
                              >
                                Appeler
                              </Button>
                            </>
                          );
                        })()}
                      </Stack>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* Pagination */}
          {totalPages > 1 && (
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              mt: 4, 
              mb: 2 
            }}>
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={handlePageChange}
                color="primary"
                size="large"
                showFirstButton
                showLastButton
                sx={{
                  '& .MuiPaginationItem-root': {
                    fontSize: '1rem',
                    fontWeight: 500
                  }
                }}
              />
            </Box>
          )}
        </>
      )}

      {/* Image Modal */}
      <ImageModal />
    </Container>
  );
};

export default VIP;
